import { clientViewService } from '@/helpers/clientViewService';

/**
 * OAuth / SDK error codes meaning the cached credentials can never be exchanged again,
 * so retrying the same token request is pointless.
 *
 * `unauthorized_client` means the Auth0 application has no `refresh_token` grant type
 * while a refresh token is still cached in localStorage, so every `POST /oauth/token`
 * answers 403. auth0-spa-js only falls back to iframe silent auth for missing or invalid
 * refresh tokens, so this error is rethrown and the unusable token stays in the cache.
 */
const UNRECOVERABLE_TOKEN_ERRORS = [
    'unauthorized_client',
    'invalid_grant',
    'login_required',
    'consent_required',
];

const RECOVERY_ATTEMPTED_KEY = 'AUTH_RECOVERY_ATTEMPTED';

type Auth0Handlers = {
    getAccessTokenSilently: () => Promise<string>;
    loginWithRedirect: (options?: any) => Promise<void>;
    logout: (options?: any) => Promise<void>;
};

let handlers: Auth0Handlers | null = null;
let recovery: Promise<never> | null = null;

export const registerAuth0Handlers = (auth0Handlers: Auth0Handlers) => {
    handlers = auth0Handlers;
};

const isUnrecoverableTokenError = (error: any): boolean => {
    if (!error) {
        return false;
    }

    // MissingRefreshTokenError carries no `error` code but means the same thing.
    if (error.name === 'MissingRefreshTokenError') {
        return true;
    }

    return UNRECOVERABLE_TOKEN_ERRORS.includes(error.error);
};

/**
 * Never settles. The browser is navigating away, so callers waiting on a token must not
 * carry on and produce their own errors on the way out.
 */
const navigatingAway = () => new Promise<never>(() => undefined);

const recoverSession = async (): Promise<never> => {
    const alreadyAttempted = sessionStorage.getItem(RECOVERY_ATTEMPTED_KEY) === 'true';

    if (alreadyAttempted) {
        clientViewService.clearClientViewData();
        sessionStorage.removeItem(RECOVERY_ATTEMPTED_KEY);

        await handlers?.logout({ logoutParams: { returnTo: window.location.origin } });

        return navigatingAway();
    }

    sessionStorage.setItem(RECOVERY_ATTEMPTED_KEY, 'true');

    // `openUrl: false` drops the unusable cached tokens without calling Auth0's logout
    // endpoint, so the Auth0 session survives and /authorize can hand back a fresh token
    // without prompting for credentials.
    await handlers?.logout({ openUrl: false });
    await handlers?.loginWithRedirect();

    return navigatingAway();
};

/**
 * Single entry point for the access token, used by both the REST and GraphQL layers.
 *
 * Called per request, so without this every failure would fire its own
 * `POST /oauth/token` and an unusable refresh token would turn into a flood of 403s.
 * Recovery runs once for all in-flight callers, and only once per browser session:
 * a second unrecoverable failure signs the user out instead of bouncing them through
 * /authorize again.
 */
export const getAccessToken = async (): Promise<string> => {
    if (!handlers) {
        throw new Error('Auth0 handlers have not been registered yet');
    }

    try {
        return await handlers.getAccessTokenSilently();
    } catch (error) {
        if (!isUnrecoverableTokenError(error)) {
            throw error;
        }

        recovery = recovery || recoverSession();

        return recovery;
    }
};
